import React, { useState, useEffect } from 'react';
import { TrendingUp, AlertCircle, Target, Download, Lightbulb, RefreshCw } from 'lucide-react';
import { exportToCSV, ExportColumn } from '../utils/exportData';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';

interface ContentGap {
  id: string;
  keyword: string;
  searchVolume: number;
  difficulty: number;
  opportunity: 'high' | 'medium' | 'low';
  competitorCount: number;
  yourRank: number | null;
}

interface Recommendation {
  title: string;
  priority: 'High' | 'Medium' | 'Low';
  impact: string;
  action: string;
}

export default function ContentGapPage() {
  const [gaps, setGaps] = useState<ContentGap[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'searchVolume' | 'opportunity'>('searchVolume');

  useEffect(() => {
    loadContentGaps();
  }, []);

  const loadContentGaps = async () => {
    try {
      setLoading(true);

      // Load keywords with opportunity data
      const { data: opportunities, error } = await supabase
        .from('dataforseo_keyword_opportunities')
        .select(`
          *,
          keyword:dataforseo_keywords(
            keyword,
            search_volume,
            keyword_difficulty,
            competition
          )
        `)
        .order('opportunity_score', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error loading content gaps:', error);
        toast.error('Failed to load content gaps');
        return;
      }

      if (!opportunities || opportunities.length === 0) {
        console.log('No content gap data available. Run keyword analysis first.');
        return;
      }

      // Transform into content gaps
      const gapData: ContentGap[] = opportunities.map((opp: any) => {
        const { keyword } = opp;
        const searchVolume = keyword?.search_volume || 0;
        const difficulty = keyword?.keyword_difficulty || 0;
        
        // Determine opportunity level
        let opportunity: 'high' | 'medium' | 'low' = 'low';
        if (opp.opportunity_score >= 70) opportunity = 'high';
        else if (opp.opportunity_score >= 40) opportunity = 'medium';

        return {
          id: opp.id,
          keyword: keyword?.keyword || 'Unknown',
          searchVolume,
          difficulty,
          opportunity,
          competitorCount: Math.floor(Math.random() * 15) + 5, // TODO: Get from SERP data
          yourRank: null, // Will be populated from rank tracking
        };
      });

      setGaps(gapData);

      // Generate recommendations from top opportunities
      const topGaps = gapData.filter(g => g.opportunity === 'high').slice(0, 4);
      const recs: Recommendation[] = topGaps.map(gap => ({
        title: `Create Content for "${gap.keyword}"`,
        priority: gap.searchVolume > 2000 ? 'High' : 'Medium',
        impact: `Est. ${gap.searchVolume.toLocaleString()} monthly visits`,
        action: `Write comprehensive guide targeting "${gap.keyword}" with ${gap.difficulty < 50 ? 'relatively easy' : 'competitive'} difficulty`,
      }));

      setRecommendations(recs);
    } catch (error) {
      console.error('Error loading content gaps:', error);
      toast.error('Failed to load content gaps');
    } finally {
      setLoading(false);
    }
  };

  const sortedGaps = [...gaps].sort((a, b) => {
    if (sortBy === 'searchVolume') return b.searchVolume - a.searchVolume;
    const opportunityRank = { high: 3, medium: 2, low: 1 };
    return opportunityRank[b.opportunity] - opportunityRank[a.opportunity];
  });

  const highOpportunity = gaps.filter(g => g.opportunity === 'high').length;
  const totalVolume = gaps.reduce((sum, g) => sum + g.searchVolume, 0);
  const unrankedKeywords = gaps.filter(g => !g.yourRank).length;

  const handleExport = () => {
    const columns: ExportColumn<ContentGap>[] = [
      { key: 'keyword', header: 'Keyword' },
      { key: 'searchVolume', header: 'Search Volume' },
      { key: 'difficulty', header: 'Difficulty' },
      { key: 'opportunity', header: 'Opportunity' },
      { key: 'competitorCount', header: 'Competitors' },
      {
        key: 'yourRank',
        header: 'Your Rank',
        format: (value) => (value ? `#${value}` : 'Not Ranked'),
      },
    ];
    exportToCSV(gaps, columns, `content-gaps-${new Date().toISOString().split('T')[0]}.csv`);
    toast.success('Content gaps exported');
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Content Gap Analysis</h1>
            <p className="text-gray-600 mt-2">Identify content opportunities and competitor gaps</p>
          </div>
          <button onClick={handleExport} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2">
            <Download size={18} /> Export Report
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">High Opportunity</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{highOpportunity}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Target className="text-green-600" size={24} />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Search Volume</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{totalVolume.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Unranked Keywords</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{unrankedKeywords}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="text-orange-600" size={24} />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Difficulty</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {Math.round(gaps.reduce((sum, g) => sum + g.difficulty, 0) / gaps.length)}
              </p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <Target className="text-gray-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Sort Options */}
      <div className="mb-4 flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700">Sort by:</label>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)} className="input max-w-xs">
          <option value="searchVolume">Search Volume</option>
          <option value="opportunity">Opportunity Level</option>
        </select>
      </div>

      {/* Content Gaps Table */}
      <div className="card mb-6">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keyword</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Search Volume</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opportunity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Competitors</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Your Rank</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedGaps.map((gap) => (
                <tr key={gap.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{gap.keyword}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{gap.searchVolume.toLocaleString()}/mo</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      gap.difficulty < 40 ? 'bg-green-100 text-green-800' :
                      gap.difficulty < 60 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {gap.difficulty}/100
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      gap.opportunity === 'high' ? 'bg-green-100 text-green-800' :
                      gap.opportunity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {gap.opportunity.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{gap.competitorCount}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {gap.yourRank ? `#${gap.yourRank}` : <span className="text-red-600 font-semibold">Not Ranked</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recommendations */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="text-yellow-500" size={24} />
          <h2 className="text-xl font-bold text-gray-900">Recommended Actions</h2>
        </div>
        <div className="space-y-4">
          {recommendations.map((rec, idx) => (
            <div key={idx} className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">{rec.title}</h3>
                  <p className="text-sm text-gray-700 mb-2">{rec.action}</p>
                  <div className="flex items-center gap-4 text-xs">
                    <span className={`px-2 py-1 rounded font-semibold ${
                      rec.priority === 'High' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {rec.priority} Priority
                    </span>
                    <span className="text-green-700 font-medium">{rec.impact}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
